import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import { ROUTES } from '@/constants/routes'
import { login } from '@/services/api/auth'
import { setAuthToken, setRefreshToken, setUser } from '@/utils/auth'

/**
 * Sign in page - two-column layout: form (left), hero image (right).
 * Wired to backend POST /api/auth/login.
 */
export default function SignIn() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await login(email, password)
      setAuthToken(data.token)
      setRefreshToken(data.refreshToken)
      setUser(data.user)
      navigate(ROUTES.DASHBOARD, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left column - Form */}
      <div className="w-full lg:w-[55%] xl:w-[50%] flex flex-col bg-white px-6 sm:px-10 py-8 lg:py-12 overflow-y-auto">
        <div className="max-w-[420px] mx-auto w-full">
          {/* Logo - centred on left side */}
          <div className="flex justify-center mb-10">
            <Link to={ROUTES.DASHBOARD}>
              <img
                src="/images/logo.png"
                alt="KAY CURZI GROUP"
                className="object-contain"
                style={{ width: 210, height: 101 }}
              />
            </Link>
          </div>

          <h2
            className="font-lexend font-bold text-2xl mb-1"
            style={{ color: '#212126' }}
          >
            Sign in to your account
          </h2>
          <p className="text-black-sub text-sm mb-6">
            Don&apos;t have account?{' '}
            <Link
              to={ROUTES.SIGNUP}
              className="font-medium hover:underline"
              style={{ color: '#2C71F6' }}
            >
              Sign up
            </Link>
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <p className="text-sm text-states-red-100" role="alert">
                {error}
              </p>
            )}
            <div>
              <label
                htmlFor="email"
                className="block font-lexend font-medium text-sm mb-1.5"
                style={{ color: '#212126' }}
              >
                Email address
              </label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-black-border bg-white text-black placeholder:text-black-sub font-sans text-sm focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                autoComplete="email"
                required
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="block font-lexend font-medium text-sm mb-1.5"
                style={{ color: '#212126' }}
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-11 rounded-lg border border-black-border bg-white text-black placeholder:text-black-sub font-sans text-sm focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-black-sub hover:text-black transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-black-border text-primary-600 focus:ring-primary-600"
                />
                <span
                  className="font-sans text-sm"
                  style={{ color: '#212126' }}
                >
                  Remember me
                </span>
              </label>
              <Link
                to="#"
                className="font-sans text-sm font-medium hover:underline"
                style={{ color: '#2C71F6' }}
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-lg font-lexend font-semibold text-sm text-white transition-colors hover:opacity-95 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#212126' }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

        </div>
      </div>

      {/* Right column - Hero image (cargo ship) */}
      <div
        className="hidden lg:block lg:flex-1 bg-cover bg-center bg-no-repeat min-h-screen"
        style={{
          backgroundImage: 'url(/images/signup-hero.jpeg)',
          backgroundColor: '#0f172a',
        }}
        role="img"
        aria-label="Cargo ship at port at night"
      />
    </div>
  )
}
